import React, { createContext, useCallback, useContext, useMemo, useState, useEffect, ReactNode } from 'react';

type StatsSettings = {
  heartRate: boolean;
  systolicBP: boolean;
  diastolicBP: boolean;
  map: boolean;
  respiratoryRate: boolean;
  temperature: boolean;
  spo2: boolean;
  hrv: boolean;
};

type HealthData = {
  heartRate: number;
  systolicBP: number;
  diastolicBP: number;
  map: number;
  respiratoryRate: number;
  temperature: number;
  spo2: number;
  hrv: number;
};

export type RhythmNetTransport = 'ws' | 'http';

type HealthHistory = { [K in keyof HealthData]: number[] };

type StatsSettingsContextType = {
  selectedStats: StatsSettings;
  toggleStat: (stat: keyof StatsSettings) => void;
  healthData: HealthData; // Added healthData
  updateHealthData: (newData: Partial<HealthData>) => void;
  // Recent trend of each stat's own value, one entry appended per
  // updateHealthData call that includes it — drives the sparkline under
  // each HealthCard's number across every page, for free.
  healthHistory: HealthHistory;
  selectedModel: string;
  updateSelectedModel: (model: string) => void;
  // RhythmNet streaming tuning — see docs/rhythmnet-settings.md
  rhythmNetTransport: RhythmNetTransport;
  updateRhythmNetTransport: (t: RhythmNetTransport) => void;
  rhythmNetHopFrames: number;
  updateRhythmNetHopFrames: (n: number) => void;
  rhythmNetBufferWindows: number;
  updateRhythmNetBufferWindows: (n: number) => void;
  // Extra display-only smoothing on top of what Navid's own pipeline
  // produces (z-scoring + overlap-average fusion) -- added this session,
  // then reported as over-smoothed (patterns look too similar across
  // signals, less peak-to-peak distinction) by reviewer feedback. Off by
  // default so the shipped default matches Navid's own signals again;
  // kept available here as an adjustable opt-in until the team decides on
  // it, per that same feedback thread.
  rhythmNetDenoiseEnabled: boolean;
  updateRhythmNetDenoiseEnabled: (enabled: boolean) => void;
};



const defaultStats: StatsSettings = {
  heartRate: true,
  systolicBP: true,
  diastolicBP: true,
  map: true,
  respiratoryRate: true,
  temperature: false,
  // Off by default — not reliable yet in the current RhythmNet version
  // (reviewer feedback). Still toggleable in settings for anyone who wants it.
  spo2: false,
  hrv: true,
};

const defaultHealthData: HealthData = {
  heartRate: 75,
  systolicBP: 120,
  diastolicBP: 80,
  map: 93,
  respiratoryRate: 16,
  temperature: 36.5,
  spo2: 98,
  hrv: 60,
};

const StatsSettingsContext = createContext<StatsSettingsContextType | undefined>(undefined);

const emptyHealthHistory: HealthHistory = {
  heartRate: [], systolicBP: [], diastolicBP: [], map: [],
  respiratoryRate: [], temperature: [], spo2: [], hrv: [],
};
const HEALTH_HISTORY_LEN = 40;

export const StatsSettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [selectedStats, setSelectedStats] = useState<StatsSettings>(defaultStats);
  const [healthData, setHealthData] = useState<HealthData>(defaultHealthData);
  const [healthHistory, setHealthHistory] = useState<HealthHistory>(emptyHealthHistory);
  const [selectedModel, setSelectedModel] = useState<string>(
    () => localStorage.getItem('sv_selected_model') ?? ''
  );

  // Stable (useCallback) deliberately: this gets threaded all the way down
  // into WebcamCard's forwardApiResponse -> startRhythmNetStream chain, each
  // a useCallback depending on the previous. An unstable reference here used
  // to cascade into a brand new identity for startRhythmNetStream on every
  // single render, which re-ran the effect that calls it and silently
  // reopened the WebSocket — a real bug that caused a reconnect every few
  // seconds during a live RhythmNet scan (only visible once the GPU was
  // actually reachable; previously masked by the GPU being down).
  const updateSelectedModel = useCallback((model: string) => {
    localStorage.setItem('sv_selected_model', model);
    setSelectedModel(model);
  }, []);

  // RhythmNet streaming tuning — persisted the same way as the model choice.
  // See docs/rhythmnet-settings.md for what each one trades off.
  const [rhythmNetTransport, setRhythmNetTransportState] = useState<RhythmNetTransport>(
    () => (localStorage.getItem('sv_rn_transport') as RhythmNetTransport) || 'ws'
  );
  const [rhythmNetHopFrames, setRhythmNetHopFramesState] = useState<number>(
    // 1 = re-run on every new frame — matches Navid's own reference demo
    // exactly (stream_demo(..., min_hop=1, ...)). RhythmNet z-scores each
    // window independently using its own mean/std, so two windows that don't
    // share almost all their frames can land at different effective scale —
    // measured 3-12x the normal sample-to-sample step at hop=160 (no
    // overlap), still ~3x at hop=80. A post-hoc calibration fix was tried
    // and found to barely help (the per-window z-score constant is
    // confounded by the window's whole content, not just the shared part,
    // so it isn't a simple affine correction). hop=1 means consecutive
    // windows share 159/160 frames, so their z-score stats are nearly
    // identical and the seam essentially disappears (measured 0.36x — below
    // the signal's own natural sample-to-sample variation). This is exactly
    // what Navid's own demo relies on for its smoothness, not an extra trick
    // on top of it. GPU cost is trivial per active user (~90-100ms/result);
    // only matters if many users stream concurrently on the single GPU.
    () => Number(localStorage.getItem('sv_rn_hop_frames')) || 1
  );
  const [rhythmNetBufferWindows, setRhythmNetBufferWindowsState] = useState<number>(
    () => Number(localStorage.getItem('sv_rn_buffer_windows')) || 1
  );
  const [rhythmNetDenoiseEnabled, setRhythmNetDenoiseEnabledState] = useState<boolean>(
    () => localStorage.getItem('sv_rn_denoise') === '1'
  );

  const updateRhythmNetTransport = useCallback((t: RhythmNetTransport) => {
    localStorage.setItem('sv_rn_transport', t);
    setRhythmNetTransportState(t);
  }, []);
  const updateRhythmNetHopFrames = useCallback((n: number) => {
    localStorage.setItem('sv_rn_hop_frames', String(n));
    setRhythmNetHopFramesState(n);
  }, []);
  const updateRhythmNetBufferWindows = useCallback((n: number) => {
    localStorage.setItem('sv_rn_buffer_windows', String(n));
    setRhythmNetBufferWindowsState(n);
  }, []);
  const updateRhythmNetDenoiseEnabled = useCallback((enabled: boolean) => {
    localStorage.setItem('sv_rn_denoise', enabled ? '1' : '0');
    setRhythmNetDenoiseEnabledState(enabled);
  }, []);

  // On first load, if no model is stored, fetch the list and pick RhythmNet (latest), else DeepPhys.
  // MODEL_DEFAULT_VERSION: users who saved an older model are moved to RhythmNet once, when it is deployed.
  useEffect(() => {
    const MODEL_DEFAULT_VERSION = '2026-10-rhythmnet';
    let stored: string | null = null;
    let migrated = false;
    try {
      stored = localStorage.getItem('sv_selected_model');
      migrated = localStorage.getItem('sv_model_default_version') === MODEL_DEFAULT_VERSION;
    } catch { /* storage unavailable */ }
    if (stored && migrated) return;
    fetch('/api/models')
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        const models: { id: string }[] = data?.models ?? [];
        const rhythmNet = models.find((m) => m.id.toLowerCase().includes('rhythmnet'));
        const preferred = rhythmNet
          ?? models.find((m) => m.id.toLowerCase().includes('deepphys'))
          ?? models[0];
        if (!preferred) return;
        if (stored && !rhythmNet) return;  // keep the user's choice until RhythmNet is live
        localStorage.setItem('sv_selected_model', preferred.id);
        if (rhythmNet) localStorage.setItem('sv_model_default_version', MODEL_DEFAULT_VERSION);
        setSelectedModel(preferred.id);
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleStat = useCallback((stat: keyof StatsSettings) => {
    setSelectedStats((prev) => ({
      ...prev,
      [stat]: !prev[stat],
    }));
  }, []);

  // RhythmNet at hop=1 calls updateHealthData up to ~30x/sec during live
  // monitoring. A sparkline doesn't need a sample every single tick — the
  // EMA-smoothed value barely moves frame to frame anyway — and pushing a
  // new array on every call at that rate was a real cost: sustained small-
  // allocation churn across up to 6 arrays, 30x/sec, which showed up as
  // periodic stalls (a GC pause pattern) during a live scan. Throttled to
  // ~2 samples/sec, which is still plenty dense for a trend line.
  const HEALTH_HISTORY_MIN_INTERVAL_MS = 500;
  const lastHistoryPushAtRef = React.useRef(0);

  const updateHealthData = useCallback((newData: Partial<HealthData>) => {
    setHealthData((prev) => ({
      ...prev,
      ...newData,
    }));
    const now = Date.now();
    if (now - lastHistoryPushAtRef.current < HEALTH_HISTORY_MIN_INTERVAL_MS) return;
    lastHistoryPushAtRef.current = now;
    setHealthHistory((prev) => {
      const next = { ...prev };
      (Object.keys(newData) as (keyof HealthData)[]).forEach((key) => {
        const v = newData[key];
        if (typeof v !== 'number' || !Number.isFinite(v)) return;
        const arr = prev[key];
        next[key] = arr.length >= HEALTH_HISTORY_LEN ? [...arr.slice(1), v] : [...arr, v];
      });
      return next;
    });
  }, []);

  // Memoized so the Provider only produces a new context value when
  // something in it actually changed — without this, every Provider
  // re-render (which happens on every state update anywhere above) handed
  // every consumer a brand new value object, forcing every single page and
  // every WebcamCard instance to re-render regardless of whether they used
  // the piece of state that changed.
  const value = useMemo<StatsSettingsContextType>(() => ({
    selectedStats, toggleStat, healthData, updateHealthData, healthHistory, selectedModel, updateSelectedModel,
    rhythmNetTransport, updateRhythmNetTransport,
    rhythmNetHopFrames, updateRhythmNetHopFrames,
    rhythmNetBufferWindows, updateRhythmNetBufferWindows,
    rhythmNetDenoiseEnabled, updateRhythmNetDenoiseEnabled,
  }), [
    selectedStats, toggleStat, healthData, updateHealthData, healthHistory, selectedModel, updateSelectedModel,
    rhythmNetTransport, updateRhythmNetTransport,
    rhythmNetHopFrames, updateRhythmNetHopFrames,
    rhythmNetBufferWindows, updateRhythmNetBufferWindows,
    rhythmNetDenoiseEnabled, updateRhythmNetDenoiseEnabled,
  ]);

  return (
    <StatsSettingsContext.Provider value={value}>
      {children}
    </StatsSettingsContext.Provider>
  );
};

export const useStatsSettings = () => {
  const context = useContext(StatsSettingsContext);
  if (!context) {
    throw new Error('useStatsSettings must be used within a StatsSettingsProvider');
  }
  return context;
};
